import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiCalendarCheck, mdiCellphone, mdiMapMarker, mdiEmailOutline, mdiLinkVariant } from '@mdi/js';
import BaseModal from '../components/BaseModal';
import { toast } from 'react-toastify';
import { instructorApi } from '../api/instructor';
import StoreBanner from '../components/store-banner';
import BookingOptionModal from '../components/BookingOptionModal';
import TimeSlotPickerModal from '../components/TimeSlotPickerModal';
import TipModal from '../components/TipModal';
import PublicBookingCalendar from '../components/PublicBookingCalendar';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyFromMinor, formatCurrency } from '../utils/currency';
import danceLocaleIcon from '../assets/dance_locale_3-200h.png';
import defaultCover from '../assets/instructor_default_cover.jpg';
import avatarPlaceholder from '../assets/avatar-placeholder.png';
import instagramGlyph from '../assets/Instagram_Glyph_Gradient_small.png';
import facebookLogo from '../assets/Facebook_Logo_Primary_small.png';
import './InstructorPublicProfile.css';

const InstructorPublicProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSearch = location.state?.from === 'search';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDownloadBanner, setShowDownloadBanner] = useState(false);
  const [showBookingOptionModal, setShowBookingOptionModal] = useState(false);
  // Phase 2: Booking flow state management
  // Visitors handed off from the mobile app (guest booking flow) arrive with
  // ?returnApp=mobile — offering "Open in App" to them is circular, so skip
  // the app-vs-web choice modal entirely and book on web directly.
  const [hasChosenWebBooking, setHasChosenWebBooking] = useState(
    () => new URLSearchParams(window.location.search).get('returnApp') === 'mobile'
  );
  const [selectedDay, setSelectedDay] = useState(null);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ senderName: '', senderEmail: '', message: '' });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const { isAuthenticated } = useAuth();

  useMobileMenu();

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    try {
      const profile = await instructorApi.getPublicProfile(id);
      setProfile(profile);
    } catch (err) {
      setError('Failed to load instructor profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle "Open in App" choice - Fire deep link logic
  const handleOpenInApp = () => {
    setShowBookingOptionModal(false);
    const deepLinkUrl = `dancelocale://instructor/${id}`;

    // Detect if app is installed
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      // iOS: Try universal link first, fall back to App Store
      window.location.href = deepLinkUrl;
      setTimeout(() => {
        window.location.href = 'https://apps.apple.com/au/app/dance-local%C3%A9/id6748289759';
      }, 1500);
    } else if (isAndroid) {
      // Android: Try intent URL
      window.location.href = `intent://instructor/${id}#Intent;scheme=dancelocale;package=com.yadawn.dancelocale;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.yadawn.dancelocale;end`;
    } else {
      // Desktop: Show banner to download app
      setShowDownloadBanner(true);
    }
  };

  // Handle "Book on Website" choice - Remember preference and show time slots
  const handleBookOnWeb = () => {
    setShowBookingOptionModal(false);
    setHasChosenWebBooking(true); // ✅ Remember user chose web booking

    // Show time slot picker for the day they originally clicked
    if (selectedDay) {
      setShowTimeSlotModal(true);
    }
  };

  // Handle calendar slot click - Smart routing based on user preference
  const handleSlotClick = (day) => {
    // Instructor not accepting public bookings — calendar is read-only. Safety net
    // in case any calendar variant still fires a click.
    if (profile?.isAcceptingBookings === false) {
      return;
    }
    // No available slots - do nothing
    if (!day.slots || day.slots.length === 0) {
      return;
    }

    // Store selected day for later use
    setSelectedDay(day);

    if (!hasChosenWebBooking) {
      // FIRST TIME: Show app vs web choice modal
      setShowBookingOptionModal(true);
    } else {
      // SUBSEQUENT TIMES: Go straight to time slot picker
      setShowTimeSlotModal(true);
    }
  };

  // Handle time slot confirmation - Navigate to booking confirmation page
  const handleConfirmTimeSlots = (selectedSlots) => {
    setShowTimeSlotModal(false);

    // Navigate to booking confirmation page with selected slots
    navigate(`/booking-confirm/${id}`, {
      state: {
        instructorId: id,
        instructorName: profile.name,
        date: selectedDay.dateKey,
        dateDisplay: selectedDay.date.toFormat('EEEE, MMMM d, yyyy'),
        slots: selectedSlots,
        hourlyRate: profile.instructorProfile.hourlyRate?.amount ?? 0,
        currency: profile.instructorProfile.hourlyRate?.currency ?? 'AUD',
        // Pick the instructor's DEFAULT preferred location (flagged isDefault),
        // not just index 0. Falls back to first active location, then to a
        // placeholder. This is what the instructor sees as their default — and
        // it's the one carrying the right venueHire data for the price card.
        location: (
          (profile.instructorProfile.preferredLocations || []).find(l => l.isDefault && l.isActive !== false) ||
          (profile.instructorProfile.preferredLocations || []).find(l => l.isActive !== false) ||
          profile.instructorProfile.preferredLocations?.[0] ||
          { description: 'Location TBD', address: '' }
        ),
        travelPeriod: selectedDay.travelPeriod, // Pass travel period if exists
        paymentTiming: profile.bookingSettings?.paymentTiming || 'after-lesson',
        // A mascot (demo) instructor: the confirm page runs as a demo and books nothing.
        isDemo: profile.instructorProfile.isMascot === true,
        // Approval hint (PRP booking-confirmation-trust-signal-rethink): show the "may need
        // approval" notice ONLY to people who would actually be held. Approval never applies
        // to prepaid (before-lesson) or auto-approve-all bookings, and under existing-students
        // an EXISTING student is auto-confirmed — the backend tells us via viewerIsExistingStudent.
        // So an existing student sees no warning; only newcomers (existing-students) or everyone
        // (manual) do. The backend decision remains authoritative.
        requiresApproval: profile.bookingSettings?.paymentTiming !== 'before-lesson'
          && profile.bookingSettings?.approvalMode !== 'all'
          && !(profile.bookingSettings?.approvalMode === 'existing-students' && profile.viewerIsExistingStudent),
        approvalMode: profile.bookingSettings?.approvalMode || 'existing-students',
        viewerIsExistingStudent: !!profile.viewerIsExistingStudent
      }
    });
  };

  const handleSendEnquiry = async () => {
    if (!enquiryForm.senderName || !enquiryForm.senderEmail || !enquiryForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setEnquirySubmitting(true);
    try {
      const response = await instructorApi.submitPublicEnquiry(id, {
        senderName: enquiryForm.senderName,
        senderEmail: enquiryForm.senderEmail,
        message: enquiryForm.message,
      });
      if (response.ok) {
        toast.success('Message sent! The instructor will reply to your email.');
        setShowEnquiryModal(false);
        setEnquiryForm({ senderName: '', senderEmail: '', message: '' });
      } else {
        toast.error(response.data?.message || 'Failed to send message.');
      }
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  // Payment handlers moved to BookingConfirmation page (Phase 3)

  if (loading) {
    return (
      <div className="instructor-public-profile">
        <div className="profile-loading-card">
          <p style={{ textAlign: "center", color: "var(--dl-color-gray-600)" }}>Loading instructor profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return <div className="profile-error">{error || 'Instructor not found'}</div>;
  }

  const instructorProfile = profile.instructorProfile || {};
  // Use orgLocation (business address) instead of preferredLocations (teaching locations)
  const businessLocation = profile.orgLocation;
  const hasSpecializations = instructorProfile.specializations?.length > 0;

  // The hourly rate is stored as { amount (integer cents), currency }. Both readers
  // below are gated on instructorProfile.hourlyRate being present.
  const rateAmount = instructorProfile.hourlyRate?.amount;
  const rateCurrency = instructorProfile.hourlyRate?.currency;

  // Social links saved from mobile settings (instructorProfile.socialLinks).
  // Standalone clickable brand icons — only the links the instructor filled in.
  const socialLinks = instructorProfile.socialLinks || {};
  const socialEntries = [
    { key: 'instagram', img: instagramGlyph, label: 'Instagram', url: socialLinks.instagram },
    { key: 'facebook', img: facebookLogo, label: 'Facebook', url: socialLinks.facebook },
    { key: 'website', mdi: mdiLinkVariant, label: 'Website', url: socialLinks.website },
  ].filter(s => s.url && s.url.trim());

  // The public profile endpoint returns the raw instructorProfile.coverImageKey
  // (not a presigned URL), so build a displayable URL via the public og-image
  // proxy — the same never-expiring proxy the mobile search card uses.
  const apiBase = process.env.REACT_APP_API_URL || '';
  const coverImageUrl = instructorProfile.coverImageKey
    ? `${apiBase}/api/v2/og-image?imageKey=${encodeURIComponent(instructorProfile.coverImageKey)}&folder=instructor${instructorProfile.coverVersion ? `&v=${instructorProfile.coverVersion}` : ''}`
    : null;

  return (
    <div className="instructor-public-profile">
      {/* Public Header */}
      <div data-role="Header" className="home-header-container">
        <header className="home-header">
          <a href="/" className="home-logo1">
            <img alt="Dance Localé" src={danceLocaleIcon} className="home-image1" />
            <span className="nav-wordmark">Dance Localé</span>
          </a>
          <div className="home-menu1">
            <a href="/" className="home-text10">Home</a>
            <a href="/instructors" className="home-text11" style={{ color: 'var(--dl-color-orange-default)', textDecoration: 'none' }}>Search</a>
            <a href="/contact" className="home-text13">Contact</a>
          </div>
          <div className="home-container11">
            <StoreBanner />
          </div>
        </header>

        {/* Mobile Menu */}
        <div data-role="MobileMenu" className="home-mobile-menu">
          <div className="home-top">
            <div className="home-logo2">
              <img alt="logo" src={danceLocaleIcon} className="home-image2" />
            </div>
            <div data-role="CloseMobileMenu" className="home-close-menu">
              <svg viewBox="0 0 1024 1024" className="home-icon">
                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
              </svg>
            </div>
          </div>
          <div className="home-mid">
            <a href="/" className="home-text14">Home</a>
            <a href="/instructors" className="home-text15" style={{ color: 'var(--dl-color-orange-default)' }}>Search</a>
            <a href="/contact" className="home-text17">Contact</a>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="profile-content">
        <div className="profile-hero">
          <div className="profile-hero-inner" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {fromSearch && (
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (location.state?.query) params.set('query', location.state.query);
                  if (location.state?.searchLocation) params.set('location', location.state.searchLocation);
                  navigate(`/instructors?${params.toString()}`);
                }}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'linear-gradient(135deg, var(--dl-color-purple-default) 0%, #6209c6 100%)',
                  border: '1px solid var(--dl-color-purple-default)',
                  color: 'white',
                  borderRadius: '18px',
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                ← Back to search
              </button>
            )}
            <div className="profile-hero-title-group">
              <h1>Meet Your Instructor</h1>
              {/* Hint only when bookings are open. When paused, the calendar scrim
                  carries the message — don't double it up here. */}
              {profile?.isAcceptingBookings !== false && (
                <span className="profile-hero-hint">
                  <Icon path={mdiCalendarCheck} size={0.8} />
                  Click a day to book
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Two-Column Layout: Phone Mockup + Full-Size Calendar */}
        <div className="mobile-preview-container">
          {/* LEFT SIDE - Phone Mockup (Instructor Profile) */}
          <div className="phone-mockup" style={{ gridColumn: '1', gridRow: '1' }}>
            <div className="phone-bezel">
              <div className="phone-screen">
                <div className="phone-scroll-area scrollable">
                {/* Status Bar */}
                <div className="phone-status-bar sticky">
                  <span>9:41</span>
                  <div>4G • 85%</div>
                </div>

                {/* Cover banner — mirrors InstructorCard: avatar inside the cover,
                    left-aligned and vertically centred, name/location below. */}
                <div style={{
                  position: 'relative',
                  height: '150px',
                  flexShrink: 0,
                  backgroundImage: `url('${coverImageUrl || defaultCover}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: '20px',
                    width: '92px',
                    height: '92px',
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    background: 'white',
                    padding: '3px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img
                      src={profile.profileImageUrl || avatarPlaceholder}
                      alt={profile.name}
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { if (e.target) e.target.src = avatarPlaceholder; }}
                    />
                  </div>
                </div>

                {/* Name + location — positioned below the cover */}
                <div style={{ padding: '16px 20px 16px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--dl-color-text-primary)' }}>
                    {profile.name}
                  </div>
                  {businessLocation?.description && (
                    <div style={{ fontSize: '14px', color: 'var(--dl-color-text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon path={mdiMapMarker} size={0.7} color="var(--dl-color-text-muted)" />
                      <span>{businessLocation.description}</span>
                    </div>
                  )}
                </div>

                {/* Dance Styles */}
                {hasSpecializations && (
                  <div className="profile-section">
                    <h3>Dance Styles</h3>
                    <div className="specialty-tags">
                      {instructorProfile.specializations.map(style => (
                        <span key={style} className="specialty-tag">{style}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Feels Message */}
                {instructorProfile.dailyMessage && (
                  <div className="profile-section">
                    <h3>Daily Feels</h3>
                    <p>{instructorProfile.dailyMessage}</p>
                  </div>
                )}

                {/* About */}
                {instructorProfile.bio && (
                  <div className="profile-section">
                    <h3>About</h3>
                    <p>{instructorProfile.bio}</p>
                  </div>
                )}

                {/* Qualifications */}
                {instructorProfile.qualifications && (
                  <div className="profile-section">
                    <h3>Qualifications</h3>
                    <p>{instructorProfile.qualifications}</p>
                  </div>
                )}

                {/* Rate disclaimer — end of scroll, referenced by the * on the footer rate line */}
                {instructorProfile.hourlyRate && !instructorProfile.isMascot && (
                  <div className="phone-rate-disclaimer">
                    * Hourly rate shown may be for home-base bookings. Travel periods may be priced in the local currency at a rate the instructor sets. Venue hire, where it applies, is confirmed when you book.
                  </div>
                )}
                </div>

                {/* Hourly rate — fixed at the bottom of the phone screen
                    (mirrors the mobile app footer, minus the See availability button) */}
                {instructorProfile.hourlyRate && (
                  <div className="phone-rate-footer">
                    {instructorProfile.isMascot
                      ? 'Love this app? Want to help out with a gift?'
                      : <>Hourly rate {formatCurrencyFromMinor(rateAmount, rateCurrency)} +venue*</>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Full-Size Calendar (No Phone Bezel) */}
          <div className="calendar-column">
            <PublicBookingCalendar
              teacherId={id}
              onSlotClick={handleSlotClick}
              variant="desktop"
              disabled={profile?.isAcceptingBookings === false}
              onEnquire={() => setShowEnquiryModal(true)}
            />
          </div>
        </div>

        {/* Below Phones: Simplified Desktop Profile Details */}
        <div className="desktop-profile-details">
          {/* Contact Instructor — shown above pricing */}
          <div className="detail-section">
            <h2>Get in Touch with {profile.name || 'this Instructor'}</h2>
            <p style={{ color: '#6b7280', marginBottom: '12px', fontSize: '0.9rem' }}>
              Not seeing slots or want to verify availability? Send a message and they'll get back to you directly.
            </p>
            <div className="instructor-contact-row">
              <button
                onClick={() => setShowEnquiryModal(true)}
                className="btn-purple instructor-send-message-btn"
              >
                <Icon path={mdiEmailOutline} size={0.8} />
                Send a Message
              </button>

              {socialEntries.length > 0 && (
                <div className="instructor-social-links">
                  {socialEntries.map(s => (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="instructor-social-link"
                      aria-label={s.label}
                      title={s.label}
                    >
                      {s.img ? (
                        <img src={s.img} alt={s.label} />
                      ) : (
                        <Icon path={s.mdi} size={1.4} />
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {instructorProfile.isMascot ? (
            // A mascot (demo) instructor: a gift in place of pricing, and the calendar
            // above walks through booking in demo mode.
            <div className="detail-section">
              <h2>Help us out</h2>
              <p>Love this app? Want to help out with a gift? Or pick a timeslot in the calendar to see how booking works.</p>
              <button className="btn-purple" onClick={() => setShowTipModal(true)}>Give a gift</button>
            </div>
          ) : instructorProfile.hourlyRate && (
            <div className="detail-section">
              <h2>Pricing</h2>
              <p className="rate-display-large">{formatCurrencyFromMinor(rateAmount, rateCurrency)} per hour</p>
            </div>
          )}

          {instructorProfile.preferredLocations?.length > 0 && (
            <div className="detail-section">
              <h2>Teaching Locations</h2>
              {instructorProfile.preferredLocations.map((loc, index) => (
                <div key={loc.id || index} className="location-item">
                  <h3>{loc.description}</h3>
                  <p>{loc.address}</p>
                  {loc.venueHire?.enabled && (
                    <p className="venue-hire">
                      Venue hire available: {formatCurrency(loc.venueHire.amount, loc.venueHire.currency)}
                      {loc.venueHire.type === 'hourly' ? '/hour' : '/session'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* App Download CTA */}
          <div className="cta-section">
            <h2>Take Your Dance Journey Mobile</h2>
            <p>Get the Dance Localé app for instant access to instructors, real-time availability, and seamless booking on the go, and ticketing for select events.</p>
            <div className="cta-store-buttons">
              <StoreBanner />
            </div>
          </div>
        </div>
      </div>

      {/* Enquiry Modal */}
      <BaseModal
        isOpen={showEnquiryModal}
        onClose={() => setShowEnquiryModal(false)}
        title={`Message ${profile.name || 'Instructor'}`}
        icon={mdiEmailOutline}
        footer={
          <>
            <button className="base-modal-cancel-btn" onClick={() => setShowEnquiryModal(false)}>
              Cancel
            </button>
            <button
              className="base-modal-save-btn"
              onClick={handleSendEnquiry}
              disabled={enquirySubmitting}
            >
              {enquirySubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </>
        }
      >
        <div style={{ background: '#f9f5ff', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '0.83rem', color: 'var(--dl-color-purple-default)' }}>
          <strong>Want instant replies?</strong> Download the Dance Localé app to message instructors directly and get notified when new slots open up.
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.83rem', marginBottom: '14px' }}>
          Or send a message via web. The instructor will reply to your email.
        </p>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '0.83rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Your Name</label>
          <input
            type="text"
            value={enquiryForm.senderName}
            onChange={(e) => setEnquiryForm(prev => ({ ...prev, senderName: e.target.value }))}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
            placeholder="Your name"
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '0.83rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Your Email</label>
          <input
            type="email"
            value={enquiryForm.senderEmail}
            onChange={(e) => setEnquiryForm(prev => ({ ...prev, senderEmail: e.target.value }))}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
            placeholder="your@email.com"
          />
        </div>
        <div>
          <label style={{ fontSize: '0.83rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Message</label>
          <textarea
            value={enquiryForm.message}
            onChange={(e) => setEnquiryForm(prev => ({ ...prev, message: e.target.value }))}
            rows={4}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.88rem', resize: 'vertical', boxSizing: 'border-box' }}
            placeholder="Hi, I'm interested in booking lessons with you..."
          />
        </div>
      </BaseModal>

      {/* Booking Option Modal */}
      <BookingOptionModal
        isOpen={showBookingOptionModal}
        onClose={() => setShowBookingOptionModal(false)}
        onOpenApp={handleOpenInApp}
        onContinueWeb={handleBookOnWeb}
        appSub={
          isAuthenticated
            ? 'Faster rebooking & booking history'
            : 'Create a free account for faster booking & lesson reminders'
        }
      />

      {/* Time Slot Picker Modal - Phase 2 Revised */}
      <TimeSlotPickerModal
        isOpen={showTimeSlotModal}
        onClose={() => setShowTimeSlotModal(false)}
        selectedDay={selectedDay}
        hourlyRate={profile.instructorProfile.hourlyRate?.amount ?? 0}
        currency={profile.instructorProfile.hourlyRate?.currency ?? 'AUD'}
        onConfirm={handleConfirmTimeSlots}
        hidePrice={profile.instructorProfile.isMascot === true}
      />

      <TipModal
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        instructorId={id}
        currency={profile.instructorProfile.hourlyRate?.currency ?? 'AUD'}
        isAuthenticated={isAuthenticated}
      />

      {/* Download Banner (Desktop fallback) */}
      {showDownloadBanner && (
        <div className="download-banner">
          <div className="banner-content">
            <p style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Icon path={mdiCellphone} size={1} />
              Download the Dance Localé app to book lessons
            </p>
            <StoreBanner />
            <button onClick={() => setShowDownloadBanner(false)} className="banner-close">×</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default InstructorPublicProfile;
